import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Phone, ArrowRight, ArrowLeft, Wrench, Flame, Droplets, Bath, Paintbrush, Settings, Siren } from "lucide-react";
import SEO from "../components/SEO";
import { services, businessInfo } from "../data/content";
import { serviceImages } from "../data/images";
import { getBreadcrumbJsonLd } from "../data/jsonld";

const iconMap = { Wrench, Flame, Droplets, Bath, Paintbrush, Settings, Siren };

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = services.find((s) => s.slug === slug);

  if (!service) return <Navigate to="/services" replace />;

  const otherServices = services.filter((s) => s.slug !== slug).slice(0, 3);
  const Icon = iconMap[service.icon];
  const img = serviceImages[service.slug];

  return (
    <>
      <SEO
        title={service.seoTitle}
        description={service.seoDescription}
        jsonLd={getBreadcrumbJsonLd([
          { name: "Accueil", url: "/" },
          { name: "Services", url: "/services" },
          { name: service.title },
        ])}
      />

      {/* Header */}
      <section className="bg-primary text-white py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/services" className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-white mb-4 transition-colors">
            <ArrowLeft size={14} /> Retour aux services
          </Link>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-secondary flex items-center justify-center rounded-sm">
              <Icon size={28} className="text-white" />
            </div>
            <h1 className="font-heading text-3xl md:text-5xl font-bold uppercase tracking-tight" data-testid="service-detail-title">
              {service.title}
            </h1>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main content */}
            <div className="lg:col-span-2">
              {img && (
                <div className="rounded-sm overflow-hidden mb-8 h-64 md:h-80">
                  <img src={img.url} alt={img.alt} className="w-full h-full object-cover" loading="eager" />
                </div>
              )}
              <p className="text-lg text-slate-700 leading-relaxed mb-8">{service.description}</p>

              <div className="bg-muted p-6 border-l-4 border-secondary rounded-r-md mb-8">
                <h3 className="font-heading text-lg font-semibold uppercase tracking-wide mb-2">Votre avantage</h3>
                <p className="text-slate-600">{service.benefit}</p>
              </div>

              <div className="bg-secondary/5 p-8 rounded-sm border border-secondary/20">
                <h3 className="font-heading text-xl font-bold uppercase tracking-tight mb-3">
                  Besoin de ce service ?
                </h3>
                <p className="text-slate-600 mb-4">
                  Contactez-nous pour un devis gratuit et sans engagement. Nous intervenons à Meaux et dans un rayon de 25 km.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href={businessInfo.phoneTel}
                    className="inline-flex items-center justify-center gap-2 bg-secondary hover:bg-secondary/90 text-white font-bold uppercase tracking-wider py-3 px-6 rounded-sm text-sm transition-all active:scale-95"
                    data-testid="service-cta-phone"
                  >
                    <Phone size={16} /> {businessInfo.phone}
                  </a>
                  <Link
                    to="/contact"
                    className="inline-flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold uppercase tracking-wider py-3 px-6 rounded-sm text-sm transition-all"
                    data-testid="service-cta-devis"
                  >
                    Demander un devis
                  </Link>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div>
              <div className="bg-primary text-white p-6 rounded-sm mb-6">
                <h3 className="font-heading text-lg font-semibold uppercase tracking-wide mb-4">Autres services</h3>
                <div className="space-y-3">
                  {otherServices.map((s) => {
                    const SIcon = iconMap[s.icon];
                    return (
                      <Link
                        key={s.slug}
                        to={`/services/${s.slug}`}
                        className="flex items-center gap-3 text-slate-300 hover:text-secondary transition-colors text-sm"
                      >
                        <SIcon size={16} />
                        {s.title}
                      </Link>
                    );
                  })}
                  <Link to="/services" className="flex items-center gap-1 text-secondary font-bold text-sm mt-4">
                    Tous les services <ArrowRight size={14} />
                  </Link>
                </div>
              </div>

              <div className="bg-secondary text-white p-6 rounded-sm">
                <h3 className="font-heading text-lg font-semibold uppercase tracking-wide mb-2">Urgence 7j/7</h3>
                <p className="text-sm opacity-90 mb-4">Un problème urgent ? Appelez-nous directement.</p>
                <a
                  href={businessInfo.phoneTel}
                  className="inline-flex items-center gap-2 bg-white text-secondary font-bold uppercase tracking-wider py-3 px-5 rounded-sm text-sm hover:bg-slate-50 transition-all"
                >
                  <Phone size={16} /> Appeler
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
